import { Controller } from "@hotwired/stimulus"
import { savePlan } from "plan_storage"

// Plan-modes controller — wires the Redact / Draft / Trial / Password
// toolbar toggles. Each writes a flag on plan.meta (or appState in
// the legacy editor) that the PDF helper picks up via the orgInfo/
// flags payload in editor-toolbar's generatePdf.

const STORAGE_KEY = "pcp_plan_data"

const TOGGLES = {
  redact:      { metaKey: "redacted",          label: "Redact PHI" },
  draft:       { metaKey: "draftMode",         label: "Draft watermark" },
  trial:       { metaKey: "trialMode",         label: "Trial watermark" },
  password:    { metaKey: "pdfPasswordProtect", label: "Password-protect PDF" },
  // Appends a final Review / Acknowledgment / Release + signature page to the
  // PCP output. Off by default (undefined ⇒ falsy). Read by print-preview.
  releaseForm: { metaKey: "releaseForm",       label: "Review & Release page" },
  // Adds a consolidated "Open Questions" page to the Review/PDF, each item
  // linked to the section it belongs to. Read by print-preview.
  openQuestions: { metaKey: "includeOpenQuestions", label: "Open Questions in Review" }
}

export default class extends Controller {
  static targets = ["toggle"]

  connect() {
    this.plan = this._load()
    if (!this.plan.meta) this.plan.meta = {}
    this._reflect()
  }

  toggle(event) {
    const btn = event.currentTarget
    const which = btn.dataset.mode
    const cfg = TOGGLES[which]
    if (!cfg) return
    // DATA-LOSS GUARD: re-load the freshest plan before mutating + broadcasting.
    // We snapshot this.plan at connect and never listen for changes, so without
    // this reload a redact/draft toggle would write (and broadcast to every
    // listener) a connect-time-stale plan, reverting edits made since load.
    this.plan = this._load()
    if (!this.plan.meta) this.plan.meta = {}
    const next = !this.plan.meta[cfg.metaKey]
    this.plan.meta[cfg.metaKey] = next
    this._save()
    this._reflect()
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true,
      detail: { path: `meta.${cfg.metaKey}`, value: next, plan: this.plan }
    }))
    this._notify(`${cfg.label}: ${next ? "ON" : "OFF"}`)
  }

  _reflect() {
    this.toggleTargets.forEach(btn => {
      const cfg = TOGGLES[btn.dataset.mode]
      if (!cfg) return
      const on = !!this.plan.meta[cfg.metaKey]
      btn.classList.toggle("is-active", on)
      btn.setAttribute("aria-pressed", on ? "true" : "false")
      btn.title = `${cfg.label} — ${on ? "ON" : "OFF"}`
    })
  }

  _notify(msg) {
    let bar = document.getElementById("en-toast")
    if (!bar) {
      bar = document.createElement("div")
      bar.id = "en-toast"
      bar.style.cssText = "position:fixed;top:12px;right:12px;padding:10px 14px;border-radius:8px;font:600 13px/1.2 ui-sans-serif,system-ui;z-index:9999;box-shadow:0 8px 24px rgba(0,0,0,.18);background:#dcfce7;color:#166534;pointer-events:none;"
      document.body.appendChild(bar)
    }
    bar.textContent = msg
    bar.style.opacity = "1"
    clearTimeout(this._toastTimer)
    this._toastTimer = setTimeout(() => { bar.style.opacity = "0" }, 2200)
  }

  _load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : {}
    } catch (_) { return {} }
  }
  _save() {
    savePlan(this.plan)
  }
}
